/**
 * CollectionPicker – TikTok-style "Save to Collection" bottom sheet
 *
 * Shows checkbox state per collection (checked = photo already saved).
 * Tap = toggle (add or remove). Auto-close after adding to new collection.
 * All data cached in memory for instant opens.
 */

import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  fetchCollections,
  fetchCollection,
  addToCollection,
  removeFromCollection,
  createCollection,
  type CollectionMeta,
} from "../lib/api.js";

interface Props {
  photoIds: string[];
  onClose: () => void;
  onAdded?: () => void;
}

// Module-level caches
let _collectionsCache: CollectionMeta[] | null = null;
// Map<collectionId, Set<photoId>> — membership cache
let _membershipCache: Map<string, Set<string>> = new Map();
let _membershipLoaded = false;

export function CollectionPicker({ photoIds, onClose, onAdded }: Props) {
  const [collections, setCollections] = useState<CollectionMeta[]>(_collectionsCache ?? []);
  const [savedIn, setSavedIn] = useState<Set<string>>(() => {
    // Compute from cache instantly
    const inSet = new Set<string>();
    for (const [colId, members] of _membershipCache) {
      if (photoIds.some((pid) => members.has(pid))) {
        inSet.add(colId);
      }
    }
    return inSet;
  });
  const [loading, setLoading] = useState(!_collectionsCache);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Load collections + membership
  useEffect(() => {
    if (_collectionsCache) {
      setCollections(_collectionsCache);
      setLoading(false);
    }

    const load = async () => {
      try {
        const cols = await fetchCollections();
        _collectionsCache = cols;
        setCollections(cols);

        // Load membership in background (only if not already loaded)
        if (!_membershipLoaded) {
          await Promise.all(
            cols.map(async (col) => {
              try {
                const full = await fetchCollection(col.id);
                _membershipCache.set(col.id, new Set(full.photoIds));
              } catch { /* ignore */ }
            })
          );
          _membershipLoaded = true;
        }

        // Recompute savedIn from fresh cache
        const inSet = new Set<string>();
        for (const [colId, members] of _membershipCache) {
          if (photoIds.some((pid) => members.has(pid))) {
            inSet.add(colId);
          }
        }
        setSavedIn(inSet);
      } catch (err) {
        if (!_collectionsCache) setError(err instanceof Error ? err.message : "Lỗi");
      }
      setLoading(false);
    };
    void load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = useCallback(async (colId: string) => {
    const isCurrentlySaved = savedIn.has(colId);

    if (isCurrentlySaved) {
      // Remove — optimistic update, stay open
      setSavedIn((prev) => { const next = new Set(prev); next.delete(colId); return next; });
      setCollections((prev) => prev.map((c) =>
        c.id === colId ? { ...c, count: Math.max(0, c.count - photoIds.length) } : c
      ));

      // Update caches
      const members = _membershipCache.get(colId);
      if (members) { for (const pid of photoIds) members.delete(pid); }
      if (_collectionsCache) {
        _collectionsCache = _collectionsCache.map((c) =>
          c.id === colId ? { ...c, count: Math.max(0, c.count - photoIds.length) } : c
        );
      }

      // API call in background
      removeFromCollection(colId, photoIds).catch(() => {});
      onAdded?.();
    } else {
      // Add — optimistic update + auto close
      setSavedIn((prev) => new Set(prev).add(colId));

      // Update caches
      let members = _membershipCache.get(colId);
      if (!members) { members = new Set(); _membershipCache.set(colId, members); }
      for (const pid of photoIds) members.add(pid);
      if (_collectionsCache) {
        _collectionsCache = _collectionsCache.map((c) =>
          c.id === colId ? { ...c, count: c.count + photoIds.length } : c
        );
      }

      onAdded?.();
      onClose();

      // API call in background
      addToCollection(colId, photoIds).catch(() => {});
    }
  }, [photoIds, savedIn, onAdded, onClose]);

  const handleCreate = useCallback(async () => {
    if (!newName.trim()) return;
    setError(null);
    try {
      const col = await createCollection(newName.trim());
      await addToCollection(col.id, photoIds);

      // Update caches
      const meta = { ...col, count: photoIds.length };
      _collectionsCache = [...(collections), meta];
      _membershipCache.set(col.id, new Set(photoIds));

      onAdded?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Lỗi");
    }
  }, [newName, photoIds, collections, onAdded, onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <motion.div
        className="relative z-10 w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl bg-[#1A1917] shadow-2xl overflow-hidden"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 400 }}
      >
        {/* Handle bar (mobile) */}
        <div className="flex justify-center pt-2 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3">
          <h3 className="text-white font-semibold">Lưu vào Collection</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white/80 text-xl leading-none">×</button>
        </div>

        {/* Collection list */}
        <div className="max-h-[50vh] overflow-y-auto px-2">
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="w-6 h-6 border-2 border-[#B76E6E] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : collections.length === 0 && !showCreate ? (
            <div className="text-center text-white/30 text-sm py-10">
              Chưa có collection nào
            </div>
          ) : (
            <div className="space-y-1">
              {collections.map((col) => {
                const isSaved = savedIn.has(col.id);

                return (
                  <button
                    key={col.id}
                    onClick={() => { void handleToggle(col.id); }}
                    className="flex items-center gap-3 w-full px-3 py-3 rounded-xl text-left hover:bg-white/5 active:bg-white/10 transition-colors"
                  >
                    {/* Checkbox */}
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      isSaved
                        ? "bg-[#B76E6E] border-[#B76E6E]"
                        : "border-white/30"
                    }`}>
                      {isSaved && (
                        <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>

                    <span className="text-white text-sm font-medium truncate flex-1">{col.name}</span>
                    <span className="text-white/30 text-xs flex-shrink-0">{col.count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {error && (
          <div className="px-5 py-2 text-red-400 text-xs">{error}</div>
        )}

        {/* Create new */}
        <div className="px-4 py-3 border-t border-white/10">
          <AnimatePresence mode="wait">
            {showCreate ? (
              <motion.div
                key="create-form"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="flex gap-2"
              >
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Tên collection..."
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#B76E6E]/50"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void handleCreate();
                    if (e.key === "Escape") { setShowCreate(false); setNewName(""); }
                  }}
                />
                <button
                  onClick={() => { void handleCreate(); }}
                  disabled={!newName.trim()}
                  className="bg-[#B76E6E] hover:bg-[#a05e5e] disabled:opacity-30 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors"
                >
                  Tạo
                </button>
              </motion.div>
            ) : (
              <motion.button
                key="create-btn"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-[#B76E6E] hover:bg-[#B76E6E]/10 text-sm font-medium transition-colors"
              >
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Tạo collection mới
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
